import Immutable from 'immutable';
import React from 'react';

import GraphQL from '~/types/graphql';

import AdminAddFreeEmailDomainsModal from '~/components/app/AdminAddFreeEmailDomainsModal';
import BasicIcon, {
	BasicIconType,
} from '~/components/patterns/icons/BasicIcon';
import Button, {
	ButtonSize,
	ButtonStyle,
} from '~/components/patterns/buttons/Button';
import ButtonsGroup from '~/components/patterns/buttons/ButtonsGroup';
import Ellipsis from '~/components/patterns/values/Ellipsis';
import NewDatatable, {
	type NewDatatableColumnDefinitions,
} from '~/components/app/NewDatatable';
import PlainTextFieldFilter from '~/components/logic/datatables/PlainTextFieldFilter';
import WithPermission from '~/components/logic/access/WithPermission';

import {
	useKingdomSettingsFreeEmailsQuery,
} from './KingdomSettingsFreeEmailsSection.gql';

import useOpenModal from '~/hooks/useOpenModal';

import {
	ObjectType,
} from '~/model/permissions';



const KingdomSettingsFreeEmailsSection: React.FC = () => {
	const [filter, setFilter] = React.useState(Immutable.Map({
		emailDomain: '',
	}));

	const { data } = useKingdomSettingsFreeEmailsQuery();

	const freeEmails = data?.kingdomSettings?.freeEmails ?? null;

	const handleOpenAddFreeEmailModal = useOpenModal(
		() => (
			<AdminAddFreeEmailDomainsModal />
		),
		[],
	);

	const updateFilter = React.useCallback(
		(filterUpdate) => {
			setFilter((filter) => filter.merge(filterUpdate));
		},
		[],
	);

	const rows = React.useMemo(
		() => {
			return (freeEmails ?? []).filter(
				(row) => {
					return row.includes(filter.get('emailDomain'));
				},
			);
		},
		[
			filter,
			freeEmails,
		],
	);

	const columns: NewDatatableColumnDefinitions<typeof rows> = React.useMemo(
		() => {
			return [
				{
					name: 'emailDomain',
					render: {
						cell: ({ row }) => (
							<Ellipsis>
								{row}
							</Ellipsis>
						),
						filter: ({ filterName, filterWidth, ref }) => (
							<PlainTextFieldFilter
								name={filterName}
								ref={ref}
								width={filterWidth}
							/>
						),
						label: () => 'Email domain',
					},
					width: 'fill',
				},
			];
		},
		[],
	);

	return (
		<NewDatatable
			buttons={(
				<ButtonsGroup>
					<WithPermission
						action={GraphQL.ActionWithPlatform.ManageInternals}
						objectId="platform"
						objectType={ObjectType.Platform}
						showMessage={false}
					>
						{({ isAllowed, message: permissionMessage }) => (
							<Button
								disabled={isAllowed.yes === false}
								icon={(
									<BasicIcon type={BasicIconType.Plus} />
								)}
								onClick={handleOpenAddFreeEmailModal}
								size={ButtonSize.Small}
								style={ButtonStyle.Hollow}
								tooltip={permissionMessage}
								uppercase={true}
							>
								Add free emails
							</Button>
						)}
					</WithPermission>
				</ButtonsGroup>
			)}
			columns={columns}
			filter={filter}
			isLoading={freeEmails === null}
			onFilterChange={updateFilter}
			rows={rows}
			title="Free emails"
		/>
	);
};



export default KingdomSettingsFreeEmailsSection;
